/**
 * You, and the two places your settings live.
 *
 * There used to be two of these — a real dropdown in the topbar and a dead
 * display row at the foot of the sidebar that showed your name and did nothing
 * when clicked. Two controls that look like the same control, one of which is
 * inert, is worse than either alone. This is the one implementation; the
 * sidebar row IS the menu now, and the topbar copy is gone.
 *
 * The menu makes the distinction the product actually has: "Your account" is
 * the identity — name, password, the companies it belongs to — and follows you
 * everywhere. "Company settings" configures the one tenant currently open.
 */

import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  LogOut,
  Settings as SettingsIcon,
  User as UserIcon,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu';
import { SidebarMenuButton } from '@/components/shadcn/sidebar';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';

export function AccountMenu({
  collapsed,
  tooltipSide,
}: {
  collapsed?: boolean;
  tooltipSide?: 'left' | 'right';
}) {
  const { t, dir } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initial = (user?.name ?? user?.email ?? 'W').trim().charAt(0).toUpperCase() || 'W';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="lg"
          className="px-2"
          tooltip={{ children: user?.name ?? user?.email ?? '', side: tooltipSide ?? 'right' }}
        >
          <Avatar size="sm" className="rounded-md">
            <AvatarFallback className="rounded-md bg-primary text-2xs font-semibold text-primary-foreground">
              {initial}
            </AvatarFallback>
          </Avatar>
          <span className="grid min-w-0 flex-1 leading-tight">
            <span className="truncate text-[0.8125rem] font-medium">
              {user?.name ?? user?.email ?? ''}
            </span>
            <span className="truncate text-2xs text-muted-foreground">{user?.role ?? '—'}</span>
          </span>
        </SidebarMenuButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side={collapsed ? (dir === 'rtl' ? 'left' : 'right') : 'top'}
        align={dir === 'rtl' ? 'end' : 'start'}
        className="min-w-56"
      >
        <DropdownMenuLabel className="grid gap-0.5">
          <span className="truncate text-sm font-medium">{user?.name}</span>
          <span className="truncate text-xs font-normal text-muted-foreground">{user?.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/account')}>
          <UserIcon className="size-4" />
          {t('account_settings')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/settings')}>
          <SettingsIcon className="size-4" />
          {t('company_settings')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/billing')}>
          <CreditCard className="size-4" />
          {t('settings_billing')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => void logout()}>
          <LogOut className="size-4" />
          {t('logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
